import {
  useAttemptQuestionMutation,
  useSaveCodeMutation,
  useSaveSubjectiveMutation,
  useStartQuestionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useAppDispatch, useAppSelector } from "../../../../store/store";

import { QuestionProgressStatus } from "../../../../types/assessmentOngoing";
import GetHeroIcon from "@lxp-app/utils/getHeroIcon";
import { cn } from "../../../../utils/cn";
import { useShowErrors } from "../toasterWrapper";
import { extractPlainText, saveAnswers } from "../saveAnswers";
import Modal from "@lxp-app/components/atoms/modal";
import ContentDeleteModal from "@lxp-app/components/molecules/modal-children/contentDeleteModal";
import { useEffect, useState } from "react";
import AppLoader from "@lxp-app/components/atoms/app-loader/appLoader";
import { useTranslation } from "react-i18next";

interface Props {
  questionId: string;
  sectionId: string;
  number: number;
  status: QuestionProgressStatus["status"] | "viewing" | "viewing_completed";
  flagged: boolean;
  questionType: string;
  questionSubType: string;
  disableSidebarQuestionBlocks: boolean;
  setDisableSidebarQuestionBlocks: (val: boolean) => void;
  targetSection: any;
  onQuestionSwitched?: () => void;
}

const SidebarQuestionBlock = ({
  questionId,
  sectionId,
  number,
  status,
  flagged,
  disableSidebarQuestionBlocks,
  setDisableSidebarQuestionBlocks,
  targetSection,
  questionType,
  onQuestionSwitched
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const showErrors = useShowErrors();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);

  const [attemptQuestion] = useAttemptQuestionMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [saveCodeAPi] = useSaveCodeMutation();

  // const IS_OBJECTIVE_QUESTION = questionType === "objective" ? true : false;
  // const IS_FIB_QUESTION = questionSubType === "fib" ? true : false;

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );

  const responsesFib = useAppSelector(
    (store) => store.AssessmentSlice.candidateFibResponse
  );

  const isSubmitting = useAppSelector(
    (store) => store.AssessmentSlice.isSaving
  );

  useEffect(() => {
    if (isSubmitting) setDisableSidebarQuestionBlocks(true);
    else setDisableSidebarQuestionBlocks(false);
  }, [isSubmitting]);

  const callAttemptAPIHandler = () => {
    const currentQn = targetSection?.questions.filter(
      (el: any) => el.questionId == activeQuestionMeta?.questionId
    );
    const currentQnType = currentQn?.[0]?.type;
    const submissionType = currentQn?.[0]?.submissionType?.[0];

    if (currentQnType == "subjective" && submissionType == "text") {
      const subjectiveCandidateRes: any = activeQuestionMeta?.questionId
        ? responses[activeQuestionMeta.questionId!]
        : undefined;

      const typedText = extractPlainText(
        subjectiveCandidateRes?.value || ""
      ).trim();
      if (
        typedText.length > 0 &&
        typedText.split(/\s+/).filter(Boolean).length >= 40
      ) {
        handleSaveModal();
      } else saveApiCall();
    } else saveApiCall();
  };

  const saveApiCall = async () => {
    await saveAnswers(
      targetSection,
      activeQuestionMeta,
      responses,
      ongoingId,
      dispatch,
      saveSubjectiveApi,
      startQuestion,
      saveCodeAPi,
      attemptQuestion,
      responsesFib,
      false,
      handleModal,
      switchQuestion
    );
  };

  const changeQuestionHandler = () => {
    if (disableSidebarQuestionBlocks) return;
    if (activeQuestionMeta?.questionId === questionId) return;

    setDisableSidebarQuestionBlocks(true);
    callAttemptAPIHandler();
  };

  const handleModal = () => {
    if (modalOpen) setDisableSidebarQuestionBlocks(false);
    setModalOpen(!modalOpen);
  };

  const handleSaveModal = () => {
    if (saveModalOpen) setDisableSidebarQuestionBlocks(false);
    setSaveModalOpen(!saveModalOpen);
  };

  const switchQuestion = () => {
    questionType === "subjective" &&
      dispatch(
        AssessmentSliceActions.mutateCandidateResponse({
          questionId: activeQuestionMeta?.questionId!,
          answerKey: []
        })
      );
    setDisableSidebarQuestionBlocks(false);
    dispatch(
      AssessmentSliceActions.mutateActiveQuestionMeta({
        questionId: String(questionId),
        sectionId: activeQuestionMeta?.sectionId
      })
    );
    onQuestionSwitched?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          changeQuestionHandler();
        }}
        // disabled={disableSidebarQuestionBlocks}
        className={cn(
          "relative w-14 h-14 rounded-xl shadow-sm flex justify-center items-center cursor-pointer",
          status === "completed" && "bg-[#F0FDF4] text-[#00875D]",
          status === "unanswered" &&
            "bg-white text-secondary dark:border-secondary/50 dark:bg-[#3b3b3b]",
          status === "not_started" &&
            "bg-white text-secondary dark:border-secondary/50 dark:bg-[#3b3b3b]",
          status === "viewing_completed" &&
            "bg-[#F0FDF4] text-[#00875D] border-primary border-solid border-2 shadow-xl",
          status === "viewing" &&
            "bg-white border-primary border-solid border-2 text-secondary shadow-xl dark:border-secondary/50 dark:bg-[#3b3b3b]"
        )}
      >
        {flagged && (
          <GetHeroIcon
            name="FlagIcon"
            className="absolute bottom-4 left-6 w-4 h-4 text-[#AA6207] drop-shadow-sm"
          />
        )}
        <span className="text-2xl font-semibold leading-7">{number}</span>
      </button>
      <Modal
        open={modalOpen}
        setOpen={handleModal}
        dialogueClassName="sm:max-w-3xl"
      >
        <ContentDeleteModal
          isShow={modalOpen}
          show={() => {
            switchQuestion();
            handleModal();
          }}
          setOpen={handleModal}
          buttonName={t("assessment.question.switchModal.switchQuestionButton")}
          showRemarksDeleteModal={false}
          heading={t("assessment.question.switchModal.leaveHeading")}
          commentsAllowed={false}
          onClose={() => setDisableSidebarQuestionBlocks(false)}
          msg={t("assessment.question.switchModal.discardMessage")}
        />
      </Modal>
      <Modal
        open={saveModalOpen}
        setOpen={handleSaveModal}
        dialogueClassName="sm:max-w-3xl"
      >
        <ContentDeleteModal
          isShow={saveModalOpen}
          show={() => {
            saveApiCall();
            handleSaveModal();
          }}
          setOpen={handleSaveModal}
          buttonName={t("assessment.question.switchModal.saveChangesButton")}
          showRemarksDeleteModal={false}
          heading={t("assessment.question.switchModal.saveHeading")}
          commentsAllowed={false}
          onClose={() => {
            handleSaveModal();
          }}
          msg={t("assessment.question.switchModal.saveBeforeSwitchMessage")}
        />
      </Modal>
      {questionType === "subjective" &&
        !modalOpen &&
        !saveModalOpen &&
        disableSidebarQuestionBlocks && <AppLoader />}
    </>
  );
};

export default SidebarQuestionBlock;
